import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Sparkles, HeartHandshake, Shield, ShieldCheck, Eye, Flame, TrendingUp, Leaf } from "lucide-react";
import { services, domains } from "../../mock";
import { SectionTitle } from "./Helpers";

const iconMap = { HeartHandshake, Sparkles, Shield, ShieldCheck, Eye, Flame, TrendingUp, Leaf };

export function DomainesSection() {
  return (
    <section className="py-20 bg-charcoal">
      <div className="container-narrow">
        <SectionTitle
          over="Domaines d'intervention"
          title="Ce que Maître Diamma peut changer pour vous"
          sub="Des situations les plus délicates aux blocages les plus anciens, chaque cas est traité avec puissance et discrétion."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {domains.map((d) => (
            <div key={d.title} className="card-onyx rounded-xl overflow-hidden group">
              <div className="relative h-44 overflow-hidden">
                <img src={d.img} alt={`${d.title} – Maître Diamma, marabout Orléans`} className="w-full h-full object-cover object-[center_22%] group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg text-gold mb-2">{d.title}</h3>
                <p className="text-sm text-gold-soft/95 leading-relaxed">{d.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ServicesGridSection() {
  return (
    <section className="py-20 section-vignette">
      <div className="container-narrow">
        <SectionTitle
          over="Spécialités reconnues"
          title="Une réponse pour chacune de vos épreuves"
          sub="Rituels africains authentiques, transmis en secret et pratiqués avec une rigueur ancestrale."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {services.map((s) => {
            const Ico = iconMap[s.icon] || Sparkles;
            return (
              <div key={s.title} className="card-onyx rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg gold-border flex items-center justify-center mb-4">
                  <Ico className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-display text-lg text-gold mb-2">{s.title}</h3>
                <p className="text-sm text-gold-soft/95 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const FEATURED = [
  {
    id: "guerisseur",
    reversed: false,
    over: "Guérisseur traditionnel africain",
    title: "Guérir en profondeur, corps et esprit",
    text: "Là où la médecine s'arrête, l'énergie prend le relais. Maître Diamma détecte l'origine spirituelle de vos douleurs, angoisses ou blocages persistants. Fort d'une transmission ancestrale, il rétablit l'harmonie entre vous et les forces invisibles qui influencent votre vie. Ses soins traditionnels apportent apaisement, sommeil retrouvé et vitalité durable.",
    img: "/images/diamma-ecriture.jpg",
    to: "/guerisseur"
  },
  {
    id: "retour-affectif",
    reversed: true,
    over: "Retour affectif puissant",
    title: "Ramener l'être aimé, sceller votre couple",
    text: "Une rupture inattendue, un silence qui s'éternise, une trahison qui blesse ? Maître Diamma est reconnu pour la puissance et la rapidité de ses rituels de retour affectif. Il rallume les sentiments éteints, dissout les malentendus, éloigne les tiers indésirables et restaure la complicité amoureuse. De nombreux couples ont retrouvé grâce à lui une union plus forte qu'avant.",
    img: "/images/diamma-blanc.jpg",
    to: "/retour-affectif"
  },
  {
    id: "voyance",
    reversed: false,
    over: "Voyance juste & précise",
    title: "Voir clair dans votre destinée",
    text: "Grâce à ses oracles africains hérités de ses maîtres, Maître Diamma lit votre passé, comprend votre présent et éclaire votre avenir. Ses réponses sont directes, sans détour ni faux espoirs. Que vous doutiez d'un amour, d'un projet ou d'une personne, vous ressortirez de la consultation avec des repères solides et la paix intérieure.",
    img: "/images/diamma-or.jpg",
    to: "/voyance"
  }
];

export function FeaturedRowsSection() {
  return (
    <section className="py-20 bg-charcoal">
      <div className="container-narrow space-y-16">
        {FEATURED.map((f) => <FeatureRow key={f.id} {...f} />)}
      </div>
    </section>
  );
}

function FeatureRow({ over, title, text, img, reversed, to }) {
  return (
    <div className={`grid md:grid-cols-2 gap-8 md:gap-10 items-center ${reversed ? "md:[&>*:first-child]:order-2" : ""}`}>
      <div className="relative rounded-xl overflow-hidden gold-border-strong order-2 md:order-none">
        <img src={img} alt={`${title} – Maître Diamma`} className="w-full h-[280px] md:h-[380px] object-cover object-[center_22%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
      </div>
      <div className="order-1 md:order-none">
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold-deep mb-3">{over}</p>
        <h3 className="font-display text-2xl md:text-3xl gold-gradient-text mb-4">{title}</h3>
        <p className="text-gold-soft/95 leading-relaxed mb-6">{text}</p>
        <Link to={to} className="btn-ghost-gold inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm">
          En savoir plus <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
